import { CartIcon } from '@/app/common/icons/general'
import { useStudentPurses } from '@/app/pages/providers/api/usePaymentProviders'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'
import { cn } from '@/common/utilities.ts'
import { usePageDescription } from '@/layouts/usePageDescription.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { RootState } from '@/storage/store'
import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useMatch, useNavigate } from 'react-router-dom'

type PageHeaderContextProps = {
  setTitle: (title: string) => void
}

const PageHeaderContext = createContext<PageHeaderContextProps | undefined>(
  undefined
)

type PageHeaderProps = {
  className?: string
} & ChildrenProp

export const PageHeaderProvider = ({
  className,
  children,
}: PageHeaderProps) => {
  const { t: translate } = useTranslation()
  const { pageDetails, setPageDetails } = usePageDescription()
  const familyApplicationMatch = useMatch('/home/application/*')
  const noHeaderRoute = familyApplicationMatch != null
  const isHomepage = useMatch(AppRoutePaths.dashboard.index)
  const cart = useSelector((state: RootState) => state.MarketplaceCart)
  const isMarketplace = window.location.href.includes('/marketplace')
  const navigate = useNavigate()
  const application = useSelector(selectFamilyApplication)
  const programId = useSelector(selectProgramID)
  const studentPurseQuery = useStudentPurses(
    {
      program_id: programId,
      application_id: application.application_id,
    },
    {
      enabled: isMarketplace,
    }
  )

  const animateCart = useSelector(
    (state: RootState) => state.MarketplaceCart.animateCart
  )

  const setTitle = (title: string) => {
    setPageDetails((prevState) => {
      return { ...prevState, title }
    })
  }

  const hasFundedStudents =
    studentPurseQuery.isLoading || studentPurseQuery.isError
      ? false
      : (studentPurseQuery.data?.some((purse) => purse.is_funded) ?? false)

  const goToCart = () => {
    navigate(AppRoutePaths.marketplace.cart.index)
  }

  const getTotalAmount = () => {
    let totalItems = 0

    cart.items.forEach((item) => {
      totalItems += item.quantity
    })

    return totalItems
  }

  return (
    <PageHeaderContext.Provider value={{ setTitle }}>
      {noHeaderRoute ||
      (pageDetails.title === '' && pageDetails.description === '') ? (
        ''
      ) : (
        <div className="flex justify-between items-center">
          <div
            className={cn(
              'hidden flex-col justify-center gap-2 py-4 sm:flex sm:pr-[200px]',
              isHomepage ? 'flex ' : '',
              className
            )}
          >
            <h1
              className={cn(
                'text-xl font-bold leading-none text-gray-900',
                isHomepage ? 'text-2xl sm:text-4xl' : ''
              )}
            >
              {translate(pageDetails.title ?? '')}
            </h1>
            <div
              className={cn(
                'flex items-center gap-2 text-sm font-medium text-gray-500'
              )}
            >
              {translate(pageDetails.description ?? '')}
            </div>
          </div>
          <div className="relative">
            {isMarketplace && (
              <button
                disabled={!hasFundedStudents}
                className="p-2 hover:bg-gray-100 rounded-full flex gap-2 items-center disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                onClick={goToCart}
              >
                {translate('view_cart')}
                <div
                  className={`relative transition-transform duration-300 ${
                    animateCart ? 'animate-bounce scale-110' : ''
                  }`}
                >
                  <CartIcon className="w-5 h-5" />
                  {cart.items.length > 0 && (
                    <div className="absolute -top-2 -right-2">
                      <span className="relative flex h-5 w-5">
                        {animateCart && (
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                        )}
                        <span className="relative inline-flex rounded-full h-5 w-5 bg-amber-500 text-white text-xs items-center justify-center font-semibold">
                          {getTotalAmount()}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </button>
            )}
          </div>
        </div>
      )}
      {children}
    </PageHeaderContext.Provider>
  )
}

export const usePageHeader = () => {
  const context = useContext(PageHeaderContext)
  if (context === undefined)
    throw new Error('usePageHeader must be used within AppAlertProvider')
  return context
}
